"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sumAll = exports.fromPercent = exports.fromDecimal = exports.hundred = exports.zero = exports.Percent = void 0;
/* eslint-disable functional/no-classes */
const numeric_1 = require("./numeric");
/**
 * Encapsulates a percentage value.
 * Provides methods to convert between percentage and decimal values.
 * Provides operators to perform calculations with percentages.
 */
class Percent {
    _value;
    _trail;
    /**
     * Creates a new Percent instance.
     *
     * @param _value the percentage value (e.g. 50 for 50%)
     */
    constructor(_value, _trail = {
        kind: "PercentCreate"
    }) {
        this._value = _value;
        this._trail = _trail;
    }
    toString = () => `Percent(${this._value})`;
    toJSON = () => ({
        kind: "Percent",
        value: this._value,
        trail: this._trail
    });
    /**
     * @returns a clone of the Percent object with the given trail label.
     */
    withLabel = (label) => new Percent(this._value, { ...this._trail, label });
    /**
     * Overwrites the label of this Percent object.
     */
    setLabel = (label) => {
        // eslint-disable-next-line functional/no-expression-statements, functional/immutable-data
        this._trail.label = label;
        return this;
    };
    /**
     * Converts the percentage value to a decimal value.
     * The decimal value is in the range [0, 1].
     */
    toDecimal = () => new numeric_1.Numeric(this._value / 100, this._trail);
    /**
     * Converts to a percentage value.
     * The percentage value is in the range [0, 100].
     */
    toPercent = () => this._value;
    /**
     * Adds two percentages.
     */
    plus = (other) => new Percent(this._value + other._value, {
        kind: "PercentAdd",
        left: this,
        right: other
    });
    /**
     * Subtracts two percentages.
     */
    minus = (other) => new Percent(this._value - other._value, {
        kind: "PercentSub",
        left: this,
        right: other
    });
    /**
     * Multiplies a Percent object by a number.
     */
    times = (a) => new Percent(this._value * a.value(), {
        kind: "PercentTimes",
        left: this,
        right: a
    });
    /**
     * Divides a Percent object by a Numeric.
     */
    over = (a) => {
        const i = a.inverse();
        return i === undefined ? undefined : this.times(i);
    };
    /**
     * Divides a Percent object by a number.
     *
     * Returns undefined if the divisor is zero.
     */
    overPercent = (a) => this.toDecimal().over(a.toDecimal());
    /**
     * Elevates a Percent object to the power of a Numeric object.
     */
    pow = (other) => new Percent(this.toDecimal().value() ** other.value() * 100, {
        kind: "PercentPow",
        left: this,
        right: other
    });
    /**
     * @returns true if the amounts of the two Percent objects are equal.
     */
    eq = (other) => this._value === other._value;
    /**
     * @returns true if the amount of the first Percent object is
     * greater to the amount of the second Percent object.
     */
    gt = (other) => this._value > other._value;
    /**
     * @returns true if the amount of the first Percent object is
     * less to the amount of the second Percent object.
     */
    lt = (other) => other.gt(this);
    /**
     * @returns true if the amount of the first Percent object is
     * greater or equal to the amount of the second Percent object.
     */
    gte = (other) => this._value >= other._value;
    /**
     * @returns true if the amount of the first Percent object is
     * less or equal to the amount of the second Percent object.
     */
    lte = (other) => other.gte(this);
}
exports.Percent = Percent;
exports.zero = new Percent(0, {
    kind: "PercentCreate",
    label: "CONSTANT"
});
exports.hundred = new Percent(100, {
    kind: "PercentCreate",
    label: "CONSTANT"
});
/**
 * Creates a new Percent instance from a decimal value.
 * The decimal value is expected to be in the range [0, 1].
 */
const fromDecimal = (decimal, trail) => new Percent(decimal * 100, trail);
exports.fromDecimal = fromDecimal;
/**
 * Creates a new Percent instance from a percentage value.
 * The percentage value is expected to be in the range [0, 100].
 */
const fromPercent = (percent, trail) => new Percent(percent, trail);
exports.fromPercent = fromPercent;
const sumAll = (percentages) => {
    const total = percentages.reduce((acc, p) => acc + p.toPercent(), 0);
    return new Percent(total, {
        kind: "PercentSum",
        items: percentages
    });
};
exports.sumAll = sumAll;
